import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Plus, ExternalLink, Trash2, RefreshCw, Search, Sparkles, Globe, Brain, Link as LinkIcon, ChevronDown, ChevronUp, Info, X, AlertCircle, Zap } from 'lucide-react';
import { api } from '../services/api';
import { useStore } from '../store';
import CrawlProgress from '../components/CrawlProgress';
import KnowledgeGaps from '../components/KnowledgeGaps';
import QuickEntry from '../components/QuickEntry';

interface Article {
  id: string;
  title: string;
  url: string;
  content: string;
  status: 'pending' | 'indexed' | 'failed';
  created_at: string;
}

interface CrawlRule {
  id: string;
  rule_type: 'include_pattern' | 'exclude_pattern' | 'min_words' | 'max_words';
  rule_value: string;
  enabled: boolean;
}

export default function KnowledgeBase() {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin';

  const [activeTab, setActiveTab] = useState<'articles' | 'gaps' | 'quick-entry'>('articles');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [crawlMode, setCrawlMode] = useState<'single' | 'full' | 'document' | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [crawlJobId, setCrawlJobId] = useState<string | null>(null);
  const [showAdvancedRules, setShowAdvancedRules] = useState(false);
  const [rules, setRules] = useState<CrawlRule[]>([]);
  const [newIncludePattern, setNewIncludePattern] = useState('');
  const [newExcludePattern, setNewExcludePattern] = useState('');
  const [minWords, setMinWords] = useState('');
  const [maxWords, setMaxWords] = useState('');
  const [indexingProgress, setIndexingProgress] = useState<{
    total: number;
    indexed: number;
    pending: number;
    failed: number;
  } | null>(null);
  const [articleLimit, setArticleLimit] = useState<number>(100);
  const [totalArticles, setTotalArticles] = useState<number>(0);
  const [currentPlan, setCurrentPlan] = useState<string>('trial');
  const [upgradePrompt, setUpgradePrompt] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  useEffect(() => {
    loadArticles();
    loadRules();
    checkIndexingProgress();
  }, []);

  // Poll for indexing progress every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      checkIndexingProgress();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const checkIndexingProgress = async () => {
    try {
      const response = await api.getKnowledgeStats();
      const stats = response?.stats;

      // Update article limits info
      if (response?.article_limit !== undefined) setArticleLimit(response.article_limit);
      if (response?.plan) setCurrentPlan(response.plan);
      if (!stats) return; // Guard against missing stats
      if (stats.total_articles !== undefined) setTotalArticles(stats.total_articles);

      if (stats.pending_articles > 0) {
        setIndexingProgress({
          total: stats.total_articles,
          indexed: stats.indexed_articles,
          pending: stats.pending_articles,
          failed: stats.failed_articles,
        });
      } else if (indexingProgress && stats.pending_articles === 0) {
        // Indexing just completed
        setIndexingProgress(null);
        loadArticles(); // Refresh articles
      } else {
        setIndexingProgress(null);
      }
    } catch (error) {
      console.error('Failed to check indexing progress:', error);
    }
  };

  const loadRules = async () => {
    try {
      const response = await api.getCrawlRules();
      setRules(response.rules || []);
    } catch (error) {
      console.error('Failed to load crawl rules:', error);
    }
  };

  const addRule = async (ruleType: string, ruleValue: string) => {
    if (!ruleValue.trim()) return;

    try {
      await api.createCrawlRule(ruleType, ruleValue);
      await loadRules();

      // Clear input
      if (ruleType === 'include_pattern') setNewIncludePattern('');
      if (ruleType === 'exclude_pattern') setNewExcludePattern('');
    } catch (error) {
      console.error('Failed to add rule:', error);
      alert('Failed to add rule');
    }
  };

  const deleteRule = async (ruleId: string) => {
    try {
      await api.deleteCrawlRule(ruleId);
      await loadRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      alert('Failed to delete rule');
    }
  };

  const saveWordCountRules = async () => {
    try {
      // Delete existing word count rules
      const existingMinWords = rules.find(r => r.rule_type === 'min_words');
      const existingMaxWords = rules.find(r => r.rule_type === 'max_words');

      if (existingMinWords) await api.deleteCrawlRule(existingMinWords.id);
      if (existingMaxWords) await api.deleteCrawlRule(existingMaxWords.id);

      // Add new rules if set
      if (minWords) await api.createCrawlRule('min_words', minWords);
      if (maxWords) await api.createCrawlRule('max_words', maxWords);

      await loadRules();
      alert('Word count rules saved!');
    } catch (error) {
      console.error('Failed to save word count rules:', error);
      alert('Failed to save word count rules');
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const response = await api.getKnowledgeArticles();
      setArticles(response);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUrl = async () => {
    if (!newUrl.trim() || !crawlMode) return;

    try {
      if (crawlMode === 'full') {
        // Start full website crawl
        const { job_id } = await api.startFullSiteCrawl(newUrl);
        setCrawlJobId(job_id);
        setShowAddModal(false);
      } else {
        // Add single URL
        await api.crawlUrl(newUrl);
        setNewUrl('');
        setShowAddModal(false);
        setCrawlMode(null);
        loadArticles();
      }
    } catch (error: any) {
      // Check if this is a limit reached error (402)
      if (error.response?.status === 402) {
        setShowAddModal(false);
        setCrawlMode(null);
        setUpgradePrompt({
          show: true,
          message: error.response?.data?.error || t('knowledge.articleLimitReached'),
        });
      } else {
        alert(error.response?.data?.error || 'Failed to add content');
      }
      console.error('Failed to add content:', error);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) return;

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.uploadDocument(formData);

      alert(`Successfully uploaded "${response.article.title}"!`);
      setSelectedFile(null);
      setShowAddModal(false);
      setCrawlMode(null);
      loadArticles();
    } catch (error: any) {
      // Check if this is a limit reached error (402)
      if (error.response?.status === 402) {
        setShowAddModal(false);
        setCrawlMode(null);
        setSelectedFile(null);
        setUpgradePrompt({
          show: true,
          message: error.response?.data?.error || t('knowledge.articleLimitReached'),
        });
      } else {
        alert(error.response?.data?.error || 'Failed to upload document');
      }
      console.error('Failed to upload document:', error);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleUpgrade = () => {
    window.location.href = '/dashboard?tab=settings&settingsTab=billing';
  };

  const handleCrawlComplete = () => {
    setCrawlJobId(null);
    setNewUrl('');
    setCrawlMode(null);
    loadArticles();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      await api.deleteArticle(id);
      loadArticles();
    } catch (error) {
      console.error('Failed to delete article:', error);
      alert('Failed to delete article');
    }
  };

  const handleReindex = async (id: string) => {
    try {
      await api.reindexArticle(id);
      setTimeout(() => loadArticles(), 1000); // Refresh after a delay
    } catch (error) {
      console.error('Failed to reindex article:', error);
      alert('Failed to reindex article');
    }
  };

  const filteredArticles = articles.filter(
    (article) =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'indexed':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-gray-900" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{t('knowledge.title')}</h1>
                {articleLimit !== -1 && (
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    totalArticles >= articleLimit
                      ? 'bg-red-100 text-red-700'
                      : totalArticles >= articleLimit * 0.9
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {totalArticles} / {articleLimit} {t('knowledge.articles')}
                  </div>
                )}
                {articleLimit === -1 && (
                  <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                    {totalArticles} {t('knowledge.articles')} ({t('knowledge.unlimitedArticles')})
                  </div>
                )}
              </div>
              <p className="text-gray-600">{t('knowledge.manageContent')}</p>
            </div>
          </div>

          {isAdmin && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition"
            >
              <Plus className="w-5 h-5" />
              {t('knowledge.addContent')}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('articles')}
            className={`px-4 py-2 font-medium transition -mb-px ${
              activeTab === 'articles'
                ? 'text-black border-b-2 border-black'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Articles
            </div>
          </button>
          <button
            onClick={() => setActiveTab('gaps')}
            className={`px-4 py-2 font-medium transition -mb-px ${
              activeTab === 'gaps'
                ? 'text-black border-b-2 border-black'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Knowledge Gaps
            </div>
          </button>
          <button
            onClick={() => setActiveTab('quick-entry')}
            className={`px-4 py-2 font-medium transition -mb-px ${
              activeTab === 'quick-entry'
                ? 'text-black border-b-2 border-black'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Quick Entry
            </div>
          </button>
        </div>

        {/* Search - only show for articles tab */}
        {activeTab === 'articles' && (
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'articles' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-black animate-spin" />
              </div>
        ) : filteredArticles.length === 0 ? (
          searchQuery ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-500 mb-4">Try a different search term</p>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              {/* Getting Started Header */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 mb-6">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Train Your AI Assistant</h2>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Get started by adding your website content. We'll crawl your site and train your AI to answer customer questions.
                  </p>
                </div>

                {/* Steps */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                      <Globe className="w-6 h-6 text-black" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">1. Enter Your Website</h3>
                    <p className="text-sm text-gray-600">
                      Provide your website URL and we'll automatically discover all your pages
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                      <BookOpen className="w-6 h-6 text-black" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">2. We Crawl & Index</h3>
                    <p className="text-sm text-gray-600">
                      Our crawler will extract content from your pages and build your knowledge base
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                      <Brain className="w-6 h-6 text-black" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">3. AI Starts Learning</h3>
                    <p className="text-sm text-gray-600">
                      Your AI assistant will use this knowledge to answer customer questions
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                {isAdmin && (
                  <div className="text-center">
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-black to-black text-white text-lg font-semibold rounded-xl hover:from-gray-800 hover:to-gray-800 transition shadow-lg hover:shadow-xl"
                    >
                      <Globe className="w-6 h-6" />
                      Start Training Your AI
                    </button>
                    <p className="mt-4 text-sm text-gray-600">
                      Usually takes 2-5 minutes depending on your site size
                    </p>
                  </div>
                )}
              </div>

              {/* Additional Options */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Other Ways to Add Content</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-black mt-2" />
                    <div>
                      <p className="text-gray-900 font-medium">Single Page URLs</p>
                      <p className="text-sm text-gray-600">Add individual articles or help pages one at a time</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-black mt-2" />
                    <div>
                      <p className="text-gray-900 font-medium">Documentation Sites</p>
                      <p className="text-sm text-gray-600">Perfect for knowledge bases, FAQs, and help centers</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-black mt-2" />
                    <div>
                      <p className="text-gray-900 font-medium">Product Pages</p>
                      <p className="text-sm text-gray-600">Help your AI answer product-specific questions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="space-y-4">
            {filteredArticles.map((article) => (
              <div
                key={article.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {article.title}
                    </h3>
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-black hover:text-gray-900 hover:underline flex items-center gap-1 mb-2"
                    >
                      {article.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {article.content}
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          article.status
                        )}`}
                      >
                        {article.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        Added {new Date(article.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleReindex(article.id)}
                        className="p-2 text-gray-600 hover:text-black hover:bg-gray-50 rounded-lg transition"
                        title="Reindex"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {activeTab === 'gaps' && <KnowledgeGaps />}

        {activeTab === 'quick-entry' && <QuickEntry />}
      </div>

      {/* Add Content Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('knowledge.addContentTitle')}</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setCrawlMode(null);
                  setNewUrl('');
                  setSelectedFile(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!crawlMode ? (
              <div className="grid md:grid-cols-3 gap-4">
                {/* Option 1: Crawl Full Website */}
                <button
                  onClick={() => setCrawlMode('full')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition text-left group"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-gray-200 transition">
                    <Globe className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Crawl Full Website</h3>
                  <p className="text-sm text-gray-600">
                    Automatically discover and index all pages from your website. Perfect for first-time setup.
                  </p>
                </button>

                {/* Option 2: Add Single URL */}
                <button
                  onClick={() => setCrawlMode('single')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition text-left group"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-gray-200 transition">
                    <LinkIcon className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Add Single URL</h3>
                  <p className="text-sm text-gray-600">
                    Add a specific page or article to your knowledge base. Great for adding individual content.
                  </p>
                </button>

                {/* Option 3: Upload Document */}
                <button
                  onClick={() => setCrawlMode('document')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition text-left group"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-gray-200 transition">
                    <BookOpen className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Upload Document</h3>
                  <p className="text-sm text-gray-600">
                    Upload Word, text files, CSV, or HTML documents to add to your knowledge base.
                  </p>
                </button>
              </div>
            ) : crawlMode === 'document' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Choose Document
                  </label>
                  <input
                    type="file"
                    accept=".txt,.md,.pdf,.docx,.csv,.html,.htm"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Supported formats: TXT, MD, PDF, DOCX, CSV, HTML (Max 10MB)
                  </p>
                  {selectedFile && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-900">
                        <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setCrawlMode(null);
                      setSelectedFile(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setCrawlMode(null);
                      setSelectedFile(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadDocument}
                    disabled={!selectedFile || uploadLoading}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-black to-black text-white rounded-lg font-medium hover:from-gray-800 hover:to-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadLoading ? 'Uploading...' : 'Upload Document'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {crawlMode === 'full' ? 'Website URL' : 'Page URL'}
                  </label>
                  <input
                    type="url"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder={crawlMode === 'full' ? 'https://yoursite.com' : 'https://yoursite.com/article'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    autoFocus
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {crawlMode === 'full'
                      ? "We'll crawl your entire website and discover all pages automatically"
                      : "We'll add this specific page to your knowledge base"}
                  </p>
                </div>

                {/* Advanced Crawl Rules - Only show for full crawl */}
                {crawlMode === 'full' && (
                  <div className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => setShowAdvancedRules(!showAdvancedRules)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition"
                    >
                      <div className="flex items-center gap-2">
                        <Info className="w-5 h-5 text-black" />
                        <span className="font-medium text-gray-900">Advanced Crawl Rules</span>
                        <span className="text-xs text-gray-500">(Optional)</span>
                      </div>
                      {showAdvancedRules ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>

                    {showAdvancedRules && (
                      <div className="p-4 border-t border-gray-200 space-y-4">
                        {/* Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm text-blue-800">
                            <strong>Tips:</strong> Use rules to control which pages get crawled and indexed. Perfect for excluding admin pages, focusing on documentation, or filtering by content length.
                          </p>
                        </div>

                        {/* Exclude Patterns */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Exclude URL Patterns
                          </label>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={newExcludePattern}
                              onChange={(e) => setNewExcludePattern(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && addRule('exclude_pattern', newExcludePattern)}
                              placeholder="/pricing, /admin, /cart"
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            />
                            <button
                              onClick={() => addRule('exclude_pattern', newExcludePattern)}
                              className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition"
                            >
                              Add
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">Skip pages that contain these patterns in the URL</p>
                          <div className="space-y-1">
                            {rules.filter(r => r.rule_type === 'exclude_pattern').map(rule => (
                              <div key={rule.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                                <span className="text-sm text-gray-700 font-mono">{rule.rule_value}</span>
                                <button
                                  onClick={() => deleteRule(rule.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Include Patterns */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Include URL Patterns <span className="text-xs text-gray-500">(Whitelist)</span>
                          </label>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={newIncludePattern}
                              onChange={(e) => setNewIncludePattern(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && addRule('include_pattern', newIncludePattern)}
                              placeholder="/docs, /help, /support"
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            />
                            <button
                              onClick={() => addRule('include_pattern', newIncludePattern)}
                              className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition"
                            >
                              Add
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">Only crawl pages that match these patterns (if set, all others are skipped)</p>
                          <div className="space-y-1">
                            {rules.filter(r => r.rule_type === 'include_pattern').map(rule => (
                              <div key={rule.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                                <span className="text-sm text-gray-700 font-mono">{rule.rule_value}</span>
                                <button
                                  onClick={() => deleteRule(rule.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Word Count Limits */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Min Words
                            </label>
                            <input
                              type="number"
                              value={minWords}
                              onChange={(e) => setMinWords(e.target.value)}
                              placeholder="100"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 mt-1">Skip pages with fewer words</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Max Words
                            </label>
                            <input
                              type="number"
                              value={maxWords}
                              onChange={(e) => setMaxWords(e.target.value)}
                              placeholder="5000"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 mt-1">Skip pages with more words</p>
                          </div>
                        </div>
                        <button
                          onClick={saveWordCountRules}
                          className="text-sm px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                        >
                          Save Word Limits
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setCrawlMode(null);
                      setNewUrl('');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setCrawlMode(null);
                      setNewUrl('');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddUrl}
                    disabled={!newUrl.trim()}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-black to-black text-white rounded-lg font-medium hover:from-gray-800 hover:to-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {crawlMode === 'full' ? 'Start Crawling' : 'Add URL'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Crawl Progress Modal */}
      {crawlJobId && (
        <CrawlProgress
          jobId={crawlJobId}
          onComplete={handleCrawlComplete}
          onError={(error) => {
            console.error('Crawl error:', error);
            alert('Crawl failed: ' + error);
            setCrawlJobId(null);
          }}
        />
      )}

      {/* Indexing Progress Modal */}
      {indexingProgress && indexingProgress.pending > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-white animate-pulse" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Indexing Content</h3>
              <p className="text-gray-600">
                Training your AI assistant with {indexingProgress.total} articles...
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>
                  {indexingProgress.indexed} / {indexingProgress.total}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-black transition-all duration-500 ease-out"
                  style={{
                    width: `${Math.round((indexingProgress.indexed / indexingProgress.total) * 100)}%`,
                  }}
                />
              </div>
              <div className="text-center mt-2 text-lg font-semibold text-black">
                {Math.round((indexingProgress.indexed / indexingProgress.total) * 100)}%
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {indexingProgress.indexed}
                </div>
                <div className="text-xs text-green-700 mt-1">Indexed</div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {indexingProgress.pending}
                </div>
                <div className="text-xs text-yellow-700 mt-1">Pending</div>
              </div>
              {indexingProgress.failed > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {indexingProgress.failed}
                  </div>
                  <div className="text-xs text-red-700 mt-1">Failed</div>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>This may take a few minutes...</span>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {upgradePrompt.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('knowledge.articleLimitReached')}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('knowledge.currentPlan')}: <span className="font-medium capitalize">{currentPlan}</span>
              </p>
            </div>
            <p className="text-gray-600 text-center mt-4">
              {upgradePrompt.message}
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setUpgradePrompt({ show: false, message: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleUpgrade}
                className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
              >
                {t('knowledge.upgradePlan')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
